import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {dataToolSchemas} from '../../utils/schemas';

const toolName = 'publish-site' as const;

const argumentZod = z.object({
  customDomains: z
    .array(z.string())
    .optional()
    .describe(
      'Optional array of custom domain IDs to publish to. If omitted, publishes to all configured domains.'
    ),
  publishToWebflowSubdomain: z
    .boolean()
    .optional()
    .describe(
      'Whether to publish to the webflow.io subdomain. Defaults to false.'
    ),
  pageId: dataToolSchemas.pageId
    .optional()
    .describe(
      'Optional page ID for Single Page Publishing. When provided, only that page is published (CMS items are not published via single-page publishing). When omitted, the entire site is published. Requires the site to have an Enterprise entitlement, single-page publishing enabled, and at least one prior full-site publish on the main branch. Site-branch pages (working/base) cannot be published to production — merge into main first, or use stage_site_branch for preview.'
    ),
});

export const publishSiteManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Publish site',
  family: 'site',
  llmTool: {
    name: toolName,
    description:
      'Publishes the current site to production. By default this publishes the entire site. To publish only a single page (Single Page Publishing), pass the optional pageId; in that case only that page is published and CMS items are not published. Site-branch pages cannot be published here — merge into main first, or use stage_site_branch for a branch preview. This enqueues an async publish job and returns immediately. The response confirms the publish was queued, not that it completed.',
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [{resource: 'Site', action: 'publish'}],
  confirmBeforeExecuting: true,
});

export type PublishSiteArgumentType = z.infer<typeof argumentZod>;
